import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Home, Building2, Store } from "lucide-react";
import { useSession } from "@/context/SessionContext";

const TYPES = [
  { id: "home", label: "Home", Icon: Home, hint: "Apartment, villa or a single room" },
  { id: "office", label: "Office", Icon: Building2, hint: "Workspace, studio or cabins" },
  { id: "commercial", label: "Commercial", Icon: Store, hint: "Café, retail, clinic, hotel" },
];
const EXTRAS = ["Kitchen", "Dining", "Study", "Balcony", "Pooja room", "Guest room", "Home theatre", "Terrace"];

export default function ProjectDetails() {
  const navigate = useNavigate();
  const { session, update } = useSession();
  const [f, setF] = useState({
    project_type: session.project_type || "home",
    project_name: session.project_name || "",
    size_mode: session.size_mode || "rooms",
    area_sqft: session.area_sqft || "",
    bedrooms: session.bedrooms ?? 2,
    halls: session.halls ?? 1,
    extra_rooms: session.extra_rooms || [],
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleExtra = (r) => set("extra_rooms", f.extra_rooms.includes(r) ? f.extra_rooms.filter((x) => x !== r) : [...f.extra_rooms, r]);
  const valid = f.size_mode === "area" ? Number(f.area_sqft) > 0 : true;

  const next = async () => {
    await update({ ...f, area_sqft: f.size_mode === "area" ? Number(f.area_sqft) : null, bedrooms: Number(f.bedrooms), halls: Number(f.halls), step: Math.max(session.step, 1) });
    navigate("/styles");
  };

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
      <p className="eyebrow rise">Step one</p>
      <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 rise d1">Tell us about the space.</h1>

      <section className="mt-14 rise d2">
        <p className="eyebrow mb-4">What are we designing?</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {TYPES.map(({ id, label, Icon, hint }) => (
            <button
              key={id}
              data-testid={`project-type-${id}`}
              onClick={() => set("project_type", id)}
              className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${f.project_type === id ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-900"}`}
            >
              <Icon size={20} />
              <div className="font-medium mt-4">{label}</div>
              <div className={`text-xs mt-1 ${f.project_type === id ? "text-neutral-300" : "text-neutral-500"}`}>{hint}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-12 rise d3">
        <p className="eyebrow mb-4">Give it a name (optional)</p>
        <input data-testid="project-name-input" className="input-line" placeholder="e.g. Sharma residence, 3BHK Bandra" value={f.project_name} onChange={(e) => set("project_name", e.target.value)} />
      </section>

      <section className="mt-12 rise d4">
        <div className="flex items-center justify-between mb-4">
          <p className="eyebrow">How big is it?</p>
          <div className="flex rounded-full border border-neutral-300 p-0.5 text-xs">
            {[["rooms", "By rooms"], ["area", "Exact area"]].map(([m, l]) => (
              <button key={m} data-testid={`size-mode-${m}`} onClick={() => set("size_mode", m)} className={`rounded-full px-3 h-8 transition-colors ${f.size_mode === m ? "bg-neutral-900 text-white" : "text-neutral-500"}`}>{l}</button>
            ))}
          </div>
        </div>
        {f.size_mode === "area" ? (
          <div className="flex items-end gap-3">
            <input data-testid="area-input" type="number" min="1" className="input-line font-mono" placeholder="1200" value={f.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} />
            <span className="text-neutral-400 pb-3 text-sm">sq ft</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {[["bedrooms", f.project_type === "home" ? "Bedrooms" : "Cabins / rooms"], ["halls", f.project_type === "home" ? "Halls / living" : "Open areas"]].map(([k, l]) => (
              <div key={k} className="rounded-2xl border border-neutral-200 p-5">
                <div className="text-xs text-neutral-500">{l}</div>
                <div className="flex items-center justify-between mt-3">
                  <button data-testid={`${k}-minus`} onClick={() => set(k, Math.max(0, f[k] - 1))} className="h-10 w-10 rounded-full border border-neutral-300 hover:border-neutral-900 text-lg">−</button>
                  <span data-testid={`${k}-value`} className="font-display text-4xl font-light">{f[k]}</span>
                  <button data-testid={`${k}-plus`} onClick={() => set(k, Math.min(12, f[k] + 1))} className="h-10 w-10 rounded-full border border-neutral-300 hover:border-neutral-900 text-lg">+</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {f.project_type === "home" && (
        <section className="mt-12">
          <p className="eyebrow mb-4">Anything else in the plan?</p>
          <div className="flex flex-wrap gap-2">
            {EXTRAS.map((r) => (
              <button key={r} data-testid={`extra-room-${r.toLowerCase().replace(/\s/g, "-")}`} onClick={() => toggleExtra(r)} className={`chip ${f.extra_rooms.includes(r) ? "chip-on" : ""}`}>{r}</button>
            ))}
          </div>
        </section>
      )}

      <div className="mt-16 flex justify-end">
        <button data-testid="project-continue-button" onClick={next} disabled={!valid} className="btn-ink h-14 px-9">
          Show me interiors <ArrowRight size={18} />
        </button>
      </div>
    </main>
  );
}
